// SPDX-License-Identifier: AGPL-3.0-only
import { AppShell, Button, Center, Group, Loader, NavLink, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { api } from './api/client';
import { useAuth } from './auth/AuthContext';
import { Login } from './pages/Login';
import { Tenancy } from './pages/Tenancy';
import { Devices } from './pages/Devices';
import { Gateways, GatewayDetail } from './pages/Gateways';
import { AddGateway } from './pages/AddGateway';
import { Device } from './pages/Device';

/**
 * The application shell and routing.
 *
 * # PROTECTED ROUTES ARE A COURTESY, NOT A CONTROL
 *
 * Redirecting an unauthenticated visitor to the login page is good behaviour.
 * It is NOT what protects anything: a client-side route guard is code the user
 * controls, and disabling it reveals nothing, because every route's data comes
 * from an API call the server refuses without a session.
 *
 * That is worth stating plainly, because the opposite belief — "the UI hides
 * it, so it is protected" — is how client-side authorization gets treated as
 * real.
 */
export function App() {
  const { isAuthenticated, isLoading, organization, logout } = useAuth();

  if (isLoading) {
    return <Center h="100vh"><Loader /></Center>;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: 'sm', collapsed: { mobile: true } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Text fw={600}>Meshvara</Text>
          <Group>
            <Text size="sm" c="dimmed">{organization?.name}</Text>
            <Button size="xs" variant="subtle" onClick={() => void logout()}>
              Sign out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      {/* NAVIGATION IS SHORT ON PURPOSE. Every entry goes somewhere that works;
          a menu of pages that are not built yet would be a promise the product
          cannot keep, and the first empty one an operator opens is the last time
          they believe the rest. */}
      <AppShell.Navbar p="xs">
        <NavItem to="/" label="Organization" />
        <NavItem to="/devices" label="Devices" />
        <NavItem to="/gateways/add" label="Add Gateway" />
        <ServerVersion />
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<Tenancy />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/gateways" element={<Gateways />} />
          <Route path="/gateways/add" element={<AddGateway />} />
          <Route path="/gateways/:gatewayId" element={<GatewayDetail />} />
          <Route path="/devices/:deviceId" element={<Device />} />
          {/* Logging in lands here; /login is no longer reachable while
              authenticated, so a stale bookmark does not show a login form to
              someone already signed in. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  const { pathname } = useLocation();
  // The devices SECTION stays highlighted while a device page is open, so the
  // navigation reflects where the operator is rather than the exact URL.
  const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
  return <NavLink component={Link} to={to} label={label} active={active} />;
}

/**
 * The build this UI is talking to, in the bottom-left corner.
 *
 * # WHY THIS IS WORTH A CORNER OF EVERY SCREEN
 *
 * Because "which version is this?" is the first question of every support
 * conversation and every upgrade, and until now the only way to answer it was to
 * read a container log. An operator looking at a page that behaves oddly should
 * be able to say what they are looking at without leaving it.
 *
 * It comes from /healthz, which is unauthenticated — the same endpoint a load
 * balancer polls — so it renders before anyone has a session and stays correct
 * for whatever build is actually serving, rather than whatever this bundle was
 * compiled against. Those two can differ during a rolling upgrade, and the
 * SERVER's answer is the one that matters.
 *
 * Silent on failure. A version line that renders an error is worse than one that
 * renders nothing: it draws the eye to the corner of the screen for a problem
 * that is not there.
 */
function ServerVersion() {
  const [health, setHealth] = useState<{ version: string; build_time?: string } | null>(null);

  useEffect(() => {
    let live = true;
    api
      .health()
      .then((h) => {
        if (live) setHealth(h);
      })
      .catch(() => {
        /* deliberately silent — see above */
      });
    return () => {
      live = false;
    };
  }, []);

  if (!health) return null;
  return (
    <Text size="xs" c="dimmed" mt="auto" p="xs" style={{ wordBreak: 'break-word' }}>
      Meshvara {health.version}
      {health.build_time && (
        <>
          <br />
          {formatBuildTime(health.build_time)}
        </>
      )}
    </Text>
  );
}

/**
 * The build timestamp, in the reader's own timezone.
 *
 * The Server stamps UTC, because a build has one moment and a server has no
 * business guessing where anyone is. The person reading the corner of the screen
 * does have a timezone, and "was this built before or after I reported that?" is
 * far easier to answer in local time.
 *
 * An unparseable value is shown as it arrived rather than as "Invalid Date" —
 * whatever the Server actually said is more useful to whoever has to explain it.
 */
function formatBuildTime(raw: string): string {
  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return raw;
  return when.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
